// TournamentChat — extracted from TournamentDetail (Iter 30 refactor).
// Handles tournament-scoped chat: polling every 10s, cross-tab WebSocket
// hint, pinned announcements, role-tinted messages, and role-gated posting.
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { t } from "../../lib/i18n";
import { toast } from "sonner";

// Iter 33 — helper: merges list + legacy singular, DEDUPED and stable.
const modIds = (tt) => [...new Set([
  ...(tt?.moderator_player_ids || []),
  ...(tt?.moderator_player_id ? [tt.moderator_player_id] : []),
])];

export default function TournamentChat({ tournamentId, tt, user, players }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState(false);
  const boxRef = useRef(null);

  const load = () => api.get(`/tournaments/${tournamentId}/chat`).then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    if (typeof window !== "undefined") {
      const handler = (e) => {
        const ev = e?.detail?.event;
        if (ev === "chat_message" || ev === "chat_deleted") load();
      };
      window.addEventListener("beyx-tournament-event", handler);
      return () => {
        clearInterval(iv);
        window.removeEventListener("beyx-tournament-event", handler);
      };
    }
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  // Can the current user post?
  const myPid = user?.player_id;
  const isAdmin = user?.role === "admin";
  const isHost = user && players.find((p) => p.id === myPid)?.handle === tt.host_handle;
  const isMod = myPid && (
    modIds(tt).includes(myPid) || myPid === tt.moderator_player_id
  );
  const isRef = myPid && (tt.referee_player_ids || []).includes(myPid);
  const isParticipant = myPid && (tt.player_ids || []).includes(myPid);
  const canPost = !!(isAdmin || isHost || isMod || isRef || isParticipant);
  const canAnnounce = !!(isAdmin || isHost || isMod);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${tournamentId}/chat`, {
        body: text,
        kind: announcement ? "announcement" : "chat",
      });
      setBody("");
      setAnnouncement(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tournaments/${tournamentId}/chat/${id}`);
      await load();
    } catch (err) {
      toast.error(t.failed);
    }
  };

  const pinned = messages.filter((m) => m.pinned);
  const regular = messages.filter((m) => !m.pinned);

  return (
    <div data-testid="tournament-chat" className="space-y-4">
      {pinned.length > 0 && (
        <div className="space-y-2">
          {pinned.map((m) => (
            <div
              key={m.id}
              data-testid={`announce-${m.id}`}
              className="border border-[#E2FF00]/40 bg-[#E2FF00]/5 p-4"
            >
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#E2FF00]">
                📣 {t.announcement} · {m.handle} · {new Date(m.ts).toLocaleString()}
                {(isAdmin || isHost || isMod || m.user_id === user?.id) && (
                  <button onClick={() => remove(m.id)} className="ml-auto text-white/40 hover:text-[#FF0055]">×</button>
                )}
              </div>
              <div className="text-sm text-white mt-2 whitespace-pre-wrap break-words">{m.body}</div>
            </div>
          ))}
        </div>
      )}
      <div
        ref={boxRef}
        className="border border-white/10 bg-[#0C0C14] p-4 h-[420px] overflow-y-auto space-y-3"
      >
        {regular.length === 0 ? (
          <div className="text-center text-white/40 font-mono text-sm py-10">{t.chatEmpty}</div>
        ) : (
          regular.map((m) => {
            const tone =
              m.role === "admin" ? "text-[#FF0055]" :
              m.role === "host" ? "text-[#E2FF00]" :
              m.role === "mod" ? "text-[#00E5FF]" :
              m.role === "referee" ? "text-[#9B7AFF]" : "text-white/80";
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`} className="text-sm leading-relaxed">
                <span className={`font-bold ${tone}`}>{m.handle}</span>
                {m.role !== "player" && (
                  <span className="ml-1.5 text-[9px] font-mono uppercase tracking-[0.25em] text-white/40">
                    {m.role}
                  </span>
                )}
                <span className="ml-2 text-[10px] font-mono text-white/30">
                  {new Date(m.ts).toLocaleTimeString()}
                </span>
                <span className="ml-2 text-white/85 break-words">{m.body}</span>
                {(isAdmin || isHost || isMod || m.user_id === user?.id) && (
                  <button onClick={() => remove(m.id)} className="ml-2 text-white/30 hover:text-[#FF0055] text-xs">×</button>
                )}
              </div>
            );
          })
        )}
      </div>
      {canPost ? (
        <form onSubmit={send} className="flex gap-2 items-start">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={announcement ? t.announcementPlaceholder : t.chatPlaceholder}
            data-testid="chat-input"
            rows={2}
            maxLength={1000}
            className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] resize-none"
          />
          <div className="flex flex-col gap-2">
            <button
              type="submit"
              disabled={busy || !body.trim()}
              data-testid="chat-send"
              className="px-4 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.2em] text-xs disabled:opacity-40"
            >
              {announcement ? "📣" : "→"}
            </button>
            {canAnnounce && (
              <label className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/50 flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={announcement}
                  onChange={(e) => setAnnouncement(e.target.checked)}
                  data-testid="chat-announcement-toggle"
                />
                {t.pin}
              </label>
            )}
          </div>
        </form>
      ) : (
        <div className="text-[11px] font-mono text-white/40" data-testid="chat-readonly">
          {user ? t.chatJoinToPost : t.chatLoginToPost}
        </div>
      )}
    </div>
  );
}
